/**
 * html-to-image **rejects the whole capture** when it cannot embed an image, so a
 * single unloadable `<img>` anywhere on the page yields no screenshot at all. That
 * is why every bug report arrived with `files=0`: `ProfilePicture.tsx` renders the
 * SSO avatar straight from `lh3.googleusercontent.com` (mounted via
 * `Navigation.tsx`, i.e. on every authenticated page), html-to-image must inline it
 * via a CORS-allowed read, and googleusercontent blocks that.
 *
 * It rejects with a DOM `Event` rather than an `Error`, which is why the failure
 * only ever logged as `[object Event]` — no message, no stack.
 *
 * Two layers, because they cover different sets:
 *
 * - `isCapturableNode` drops images we already know will fail, so we never pay for
 *   the fetch. Measured: a cross-origin `<iframe>` is harmless, and the same `<img>`
 *   set to `hidden` still rejects (hidden nodes are walked too).
 * - `onCaptureImageError` catches the rest of the class — the library turns a
 *   rejection into a resolve when this is supplied (`lib/types.d.ts:95`). A
 *   **same-origin** image that 404s reproduces the identical failure (our own
 *   `digital-coworkers/:id/photo` 404s by design when a coworker has no photo), and
 *   no filter can predict that.
 */
export function isCapturableNode(node: Element): boolean {
  const tag = node?.tagName?.toLowerCase()

  // SVG `<image>` travels the same embed-and-reject path as `<img>`, and its href
  // lives on `href`/`xlink:href`.
  if (tag !== 'img' && tag !== 'image') return true

  const src =
    node.getAttribute('src') ??
    node.getAttribute('href') ??
    node.getAttribute('xlink:href') ??
    ''
  if (!src) return true

  // `data:` and `blob:` parse to the string "null" origin, so a bare origin
  // comparison would strip them — and the app renders both (the bug modal's own
  // screenshot preview is a `data:` URI).
  if (src.startsWith('data:') || src.startsWith('blob:')) return true

  try {
    return new URL(src, window.location.href).origin === window.location.origin
  } catch {
    // An unparseable src cannot be proven cross-origin; leave it in.
    return true
  }
}

/**
 * Supplying this makes html-to-image resolve instead of reject when an image fails
 * to load, so one broken asset degrades the screenshot rather than losing it.
 */
export function onCaptureImageError(...args: unknown[]): void {
  console.warn('Bug-report screenshot: skipped an image that failed to load', {
    detail: describeCaptureFailure(args[0]),
  })
}

/**
 * html-to-image hands a DOM `Event` to `catch`, whose default stringification says
 * nothing. Name what actually arrived so the next failure is greppable.
 */
export function describeCaptureFailure(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`

  if (typeof Event !== 'undefined' && err instanceof Event) {
    const target = err.target as { tagName?: string; src?: string } | null
    return `DOM ${err.type} event on ${target?.tagName ?? 'unknown'} src=${target?.src ?? 'none'}`
  }

  return String(err)
}
